import React, { useEffect, useState, useRef } from "react";
import {
    JOB_TERM_DEPARTMENTS,
    JOB_RESET_FILTERS_TEXT,
    JOB_RESET_ALL_FILTERS,
    JOB_PAGE_SIZE,
    JOB_NO_RESULTS_TEXT,
    JOB_PRELOADER_TEXT,
    jobCategoryFieldMapping,
    JOB_TERM_COUNTRIES,
} from "@qlik/qlik/src/lib/constants/job-constants";
import { filterLocalJobs } from "@qlik/qlik/src/lib/services/jobs/api";
import Pagination from "@qlik/qlik/src/components/Common/Pagination";
import {
    getValidatedCategoryFilters,
    scrollToElement,
    getMicrocopyValueFromKeys,
} from "@qlik/qlik/src/lib/util/helpers";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import FilterChips from "@qlik/qlik/src/components/Common/FilterChips";
import NoResults from "@qlik/qlik/src/components/Common/NoResults";
import useUrlQueryParamsState from "@qlik/qlik/src/hooks/useUrlQueryParamsState";
import JobCategoryData from "@qlik/qlik/src/graphql/queries/jobsFilters";
import DynamicCheckboxFilterGroup from "@qlik/qlik/src/components/Common/DynamicCheckboxFilterGroup";
import JobListData from "@qlik/qlik/src/graphql/queries/jobListing";
import JobCard from "./JobCard";

const JobListing = ({ data, className }) => {
    const { additionalConfiguration, backgroundColor, customId } = data || {};
    const customClassName = className ?? ""; // pass custom class
    const [categories, setCategories] = useState([]); // set of filter categories - static
    const [currentCategory, setCurrentCategory] = useState(""); // keeps track of the last filtered category

    // set initial filters
    const initialFilters = {
        page: 0,
        limit: JOB_PAGE_SIZE,
        [JOB_TERM_COUNTRIES]: [],
        [JOB_TERM_DEPARTMENTS]: [],
    };

    // set filters - This uses a shared useUrlQueryParamsState hook
    // which will add the "query" param to the url, and is not needed for the careers listing
    const [filters, setFilters] = useUrlQueryParamsState(initialFilters, jobCategoryFieldMapping);

    const postListWrapperClassName = "md:col-8 xl:col-9 xl:pl-11.25";

    // Get the Job Filters from the static query function
    const { jobFilterData } = JobCategoryData();

    // Get the Jobs data.
    const jobsData = JobListData();

    // Get the filtered jobs as well as the total count before pagination happens
    const [filteredLocalJobs, setFilteredLocalJobs] = useState([]);
    const [totalJobs, setTotalJobs] = useState(0);
    const [loading, setLoading] = useState(true);

    // set category filters with validations
    useEffect(() => {
        // construct category objects
        const categoryData = Object.entries(jobFilterData).map(([categoryName, categoryFilters]) => {
            const transformedCategoryName = categoryName.replace("_", " ");
            const categoryObj = {
                id: transformedCategoryName,
                title: transformedCategoryName,
                subTerms: [],
            };

            // add job id's as subTerms, that will be used to disable filters which don't have any jobs
            categoryObj.subTerms = categoryFilters.map(({ fieldValue, edges }) => ({
                id: fieldValue,
                title: fieldValue,
                isDisabled: false,
                items: edges.map((item) => item.node.id), // keep the resource items for each category to update filters availability
            }));

            // sort category filters in asc
            categoryObj.subTerms.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));

            return categoryObj;
        });

        // pass the categoryData to getValidatedCategoryFilters
        const validatedCategories = getValidatedCategoryFilters(
            { title: currentCategory, value: filters[currentCategory] },
            categoryData
        );
        // set categories
        setCategories(validatedCategories);

        // set job data
        const filteredJobState = filterLocalJobs(filters, jobsData);
        setFilteredLocalJobs(filteredJobState.filteredLocalJobs);
        setTotalJobs(filteredJobState.totalJobs);
        setLoading(false);
    }, [filters, currentCategory, jobFilterData]);

    const sectionRef = useRef(null);

    const handlePageChange = (selectedPage) => {
        // Update the page value in the filters state
        setFilters((prev) => ({ ...prev, page: selectedPage }));

        // Scroll to the section element with the sectionRef
        scrollToElement(sectionRef);
    };

    // doing some styling
    const {
        sectionColorMapping: { bgColor },
    } = getColorClassName(backgroundColor);
    const spacingClassName = getSpacingClassName(additionalConfiguration, "py-15");

    // filters which are reflected in filter chips (the filter elements)
    // excluding page & limit
    const getFilterChipFilters = () => {
        const filtersObj = { ...filters };
        delete filtersObj.page;
        delete filtersObj.limit;
        return filtersObj;
    };

    // update filter state when filter chip close
    const handleFilterChipClose = (filterCategory, filterValue) => {
        const currentFilterValue = filters[filterCategory];
        let updatedFilter = "";
        if (typeof currentFilterValue !== "string") {
            updatedFilter = currentFilterValue?.filter((filter) => filter !== filterValue);
        }
        setCurrentCategory(filterCategory);
        setFilters((prev) => ({ ...prev, [filterCategory]: updatedFilter }));
    };

    const resetFilters = () => {
        setCurrentCategory("");
        setFilters({
            page: 0,
            limit: JOB_PAGE_SIZE,
            ...Object.fromEntries(Object.keys(jobCategoryFieldMapping).map((key) => [[key], []])),
        });
    };

    const [
        clearFiltersText,
        clearAllFiltersText,
        noResultsText,
        preloaderText,
    ] = getMicrocopyValueFromKeys(data?.microcopyGroup, [
        JOB_RESET_FILTERS_TEXT,
        JOB_RESET_ALL_FILTERS,
        JOB_NO_RESULTS_TEXT,
        JOB_PRELOADER_TEXT,
    ]);

    return (
        <section
            ref={sectionRef}
            className={`${bgColor} ${spacingClassName} ${customClassName} scroll-m-20`}
            id={customId}
        >
            <div className="container">
                <div className="row">
                    {loading ? (
                        <div className="flex h-full items-start">
                            <NoResults className="w-full text-center text-stone font-medium" text={preloaderText} />
                        </div>
                    ) : (
                        <>
                            <div className="md:col-4 xl:col-3">
                                <div className="pb-7.5 md:pb-0">
                                    <p className="text-sm text-right">{totalJobs} results</p>
                                    {/* Render filters for each top level category */}
                                    {categories?.map(
                                        (category, i) =>
                                            category.subTerms && (
                                                <DynamicCheckboxFilterGroup
                                                    key={category.id}
                                                    category={category}
                                                    filters={filters}
                                                    setFilters={setFilters}
                                                    setCurrentCategory={setCurrentCategory}
                                                    index={i}
                                                    expandByDefault
                                                />
                                            )
                                    )}
                                    {Object.values(getFilterChipFilters())
                                        .flat()
                                        .filter(Boolean).length > 0 && (
                                        <button
                                            className="hidden md:inline-block mt-8.75 text-xs lg:text-sm font-medium text-green tracking-wide4 cursor-pointer uppercase"
                                            onClick={resetFilters}
                                        >
                                            <span className="inline-block w-3 h-3 mr-2.5 bg-closeIconGreenMd bg-no-repeat" />
                                            {clearAllFiltersText}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className={`${postListWrapperClassName}`}>
                                {Object.values(getFilterChipFilters())
                                    .flat()
                                    .filter(Boolean).length > 0 && (
                                    <div className="flex flex-wrap gap-2.5 pb-7.5 lg:pb-15 pt-5 sm:pt-0">
                                        <FilterChips
                                            filters={getFilterChipFilters()}
                                            onClose={handleFilterChipClose}
                                            onResetFilters={resetFilters}
                                            resetFiltersLabel={clearFiltersText}
                                        />
                                    </div>
                                )}
                                {filteredLocalJobs?.length === 0 && (
                                    <div className="flex h-full items-start">
                                        <NoResults
                                            className="w-full text-center text-stone font-medium"
                                            text={noResultsText}
                                        />
                                    </div>
                                )}
                                <div className="grid gap-6 grid-cols-1 lg:gap-x-7.5 lg:gap-y-7">
                                    {filteredLocalJobs?.map((job) => (
                                        <JobCard key={job.id} job={job} />
                                    ))}
                                </div>
                                <div>
                                    <Pagination
                                        currentPage={filters.page}
                                        limit={filters.limit}
                                        total={totalJobs}
                                        handlePageChange={handlePageChange}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};

export default JobListing;
